
import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import { cn } from "@/lib/utils"

interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  value?: number
  gradientFrom?: string
  gradientTo?: string
  animationType?: "pulse" | "flow" | "glow" | "none"
  thickness?: "thin" | "default" | "thick"
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ 
  className, 
  value, 
  gradientFrom, 
  gradientTo,
  animationType = "none",
  thickness = "default",
  ...props 
}, ref) => {
  const [isValid, setIsValid] = React.useState(true);

  React.useEffect(() => {
    // Proteção contra manipulação externa do DOM (Google Translate, etc.)
    const checkDomIntegrity = () => {
      try {
        if (ref && typeof ref === 'object' && ref.current) {
          const element = ref.current;
          if (!element.parentNode || element.parentNode !== element.parentElement) {
            setIsValid(false);
            return false;
          }
        }
        return true;
      } catch (error) {
        console.warn('DOM integrity check failed:', error);
        setIsValid(false);
        return false;
      }
    };

    const interval = setInterval(checkDomIntegrity, 1000);
    return () => clearInterval(interval);
  }, [ref]);

  if (!isValid) {
    return null;
  }
  // Define height based on thickness
  const heightClass = {
    thin: "h-2",
    default: "h-4",
    thick: "h-6"
  }[thickness]

  // Define animation styles
  const getAnimationClass = () => {
    switch (animationType) {
      case "pulse":
        return "animate-pulse";
      case "flow":
        return "bg-[length:200%_auto] animate-[gradient_2s_ease_infinite]";
      case "glow":
        return "animate-premium-glow";
      default:
        return "";
    }
  }

  // Set gradient or solid background style
  const getBackgroundStyle = () => {
    if (gradientFrom && gradientTo) {
      return {
        background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`,
      }
    }
    return { backgroundColor: "var(--primary)" }
  }

  try {
    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          `relative w-full overflow-hidden rounded-full bg-secondary`,
          heightClass,
          className
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            "h-full w-full flex-1 transition-all relative overflow-hidden",
            getAnimationClass()
          )}
          style={{
            transform: `translateX(-${100 - (value || 0)}%)`,
            ...getBackgroundStyle()
          }}
        >
          {/* Shimmer effect overlay */}
          <div
            className="absolute inset-0 pointer-events-none animate-shimmer"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              width: '50%',
              animation: 'shimmer 2.5s infinite ease-in-out 1.5s'
            }}
          />
        </ProgressPrimitive.Indicator>
      </ProgressPrimitive.Root>
    )
  } catch (error) {
    console.error('Progress component render error:', error);
    return null;
  }
})
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
